import { toast } from "$global-providers/toast-provider";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import type { EpocusRole } from "elephant-web/gql";
import { phone } from "phone";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  selectActiveProgramId,
  useMyPrograms,
} from "../../../../_lib/use-active-program";
import {
  linkToEpocusManageUsers,
  linkToEpocusSettings,
} from "../../../../links";
import { normaliseNpi } from "../../../_lib/npi";
import { useCanManageProgramUsers } from "../../../_lib/use-can-manage-program-users";
import {
  useEpocusProgramUsersQuery,
  useEpocusUpdateUserMutation,
} from "../../_lib/__generated__/program-users-operations.gql";
import {
  ProgramUserForm,
  type ProgramUserFormValues,
} from "../../_lib/program-user-form";

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 600,
  margin: "0 auto",
});

const CenteredState = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 16,
  padding: 32,
});

export const EpocusEditUserPage: FC = () => {
  const canManageProgramUsers = useCanManageProgramUsers();
  const { userId: epocusUserId } = useParams<{ userId: string }>();
  const { programs, loading: programsLoading } = useMyPrograms();
  const programId = selectActiveProgramId(programs);
  const navigate = useNavigate();

  const { data, loading } = useEpocusProgramUsersQuery({
    variables: { programId: programId ?? "" },
    skip: !programId,
    fetchPolicy: "cache-and-network",
  });

  const [updateUser, { loading: savingUser }] = useEpocusUpdateUserMutation({
    onError: (e) => toast.error(`Couldn't update user: ${e.message}`),
  });

  if (!canManageProgramUsers) {
    return <Navigate to={linkToEpocusSettings()} replace />;
  }

  const member = data?.listProgramUsers.find(
    (m) => m.epocusUserId === epocusUserId,
  );

  const handleSubmit = async (values: ProgramUserFormValues) => {
    if (!epocusUserId || !programId || !values.role) return;

    await updateUser({
      variables: {
        input: {
          epocusUserId,
          programId,
          firstName: values.firstName,
          lastName: values.lastName,
          telephone: values.telephone
            ? phone(values.telephone).phoneNumber || values.telephone
            : undefined,
          npi: normaliseNpi(values.npi) || null,
          role: values.role as EpocusRole,
        },
      },
      refetchQueries: ["EpocusProgramUsers"],
    });
    toast.success("User updated");
    navigate(linkToEpocusManageUsers());
  };

  const backButton = (
    <Button
      onClick={() => navigate(linkToEpocusManageUsers())}
      style={{
        textTransform: "none",
        padding: "4px 0",
        minWidth: 0,
        alignSelf: "flex-start",
      }}
    >
      ← Back to users
    </Button>
  );

  if (programsLoading || loading) {
    return (
      <PageRoot>
        {backButton}
        <CenteredState>
          <CircularProgress />
        </CenteredState>
      </PageRoot>
    );
  }

  if (!programId) {
    return (
      <PageRoot>
        {backButton}
        <CenteredState>
          <Typography>Couldn't determine your program.</Typography>
        </CenteredState>
      </PageRoot>
    );
  }

  if (!member) {
    return (
      <PageRoot>
        {backButton}
        <CenteredState>
          <Typography>Couldn't load this user.</Typography>
        </CenteredState>
      </PageRoot>
    );
  }

  const initialValues: Partial<ProgramUserFormValues> = {
    firstName: member.user.firstName,
    lastName: member.user.lastName,
    telephone: member.user.telephone ?? "",
    email: member.user.email,
    npi: member.user.npi ?? "",
    role: member.role,
  };

  return (
    <PageRoot>
      {backButton}

      <Typography variant="h5" style={{ fontWeight: 700 }}>
        Edit user
      </Typography>

      <ProgramUserForm
        initialValues={initialValues}
        submitLabel="Save changes"
        submitting={savingUser}
        emailEditable={false}
        npiEditable
        onSubmit={handleSubmit}
      />
    </PageRoot>
  );
};
